import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useToast } from './Toast';
import { sendFeedback } from '../api/feedbackApi';
import { collectFeedbackContext } from '../utils/feedbackContext';

// Типы обращений. emoji + короткий лейбл для сегмент-контрола.
const TYPES = [
  { key: 'BUG', label: 'Проблема', emoji: '🐛' },
  { key: 'IDEA', label: 'Идея', emoji: '💡' },
  { key: 'QUESTION', label: 'Вопрос', emoji: '❓' },
];

const PLACEHOLDERS = {
  BUG: 'Что пошло не так? Что вы делали перед этим?',
  IDEA: 'Опишите идею или предложение…',
  QUESTION: 'Ваш вопрос…',
};

const MAX_LEN = 4000;

/**
 * Глобальный виджет обратной связи: небольшая кнопка у левого края экрана,
 * по клику — нижний лист (bottom-sheet) с формой (тип + текст).
 *
 * Смонтирован один раз в App.js внутри Router/ToastProvider, поэтому доступен на всех
 * страницах и не требует правок в шапках отдельных страниц. Кнопка стоит у левого
 * края ближе к низу (с запасом над нижними панелями и тостами) — не конфликтует ни
 * с шапками (шестерёнка/создать), ни с фиксированными панелями (ScoreModal, «Старт»).
 *
 * Скрыт на публичном просмотре турнира (/public/*) — там нет Telegram и авторизации.
 */
const FeedbackWidget = () => {
  const location = useLocation();
  const { addToast } = useToast();

  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false); // для слайд-анимации листа
  const [type, setType] = useState('BUG');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) { setShown(false); return; }
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Не показываем на публичных страницах (без Telegram/initData).
  if (location.pathname.startsWith('/public')) return null;

  const close = () => {
    if (sending) return;
    setOpen(false);
  };

  const handleSubmit = async () => {
    const text = message.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await sendFeedback({
        type,
        message: text,
        route: location.pathname + location.search,
        context: collectFeedbackContext(),
      });
      addToast('Спасибо! Обращение отправлено 🙌', 'success');
      setMessage('');
      setType('BUG');
      setOpen(false);
    } catch (err) {
      if (err.status === 429) {
        addToast('Слишком много обращений подряд. Попробуйте чуть позже.', 'warning');
      } else {
        addToast(`Не удалось отправить: ${err.message}`, 'error');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Кнопка вызова — у левого края, ближе к низу (с запасом над нижними панелями и тостами) */}
      <button
        onClick={() => setOpen(true)}
        title="Сообщить о проблеме или предложить идею"
        aria-label="Обратная связь"
        style={{
          position: 'fixed',
          bottom: 'calc(96px + env(safe-area-inset-bottom))',
          left: 0,
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 30,
          height: 34,
          border: 'none',
          borderRadius: '0 10px 10px 0',
          background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
          color: '#fff',
          cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(79,70,229,0.35)',
          opacity: open ? 0 : 0.92,
          transition: 'opacity 0.15s',
          pointerEvents: open ? 'none' : 'auto',
        }}
      >
        {/* иконка «сообщение» */}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      {/* Bottom sheet с формой */}
      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
          {/* Backdrop */}
          <div
            onClick={close}
            style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
              opacity: shown ? 1 : 0, transition: 'opacity 0.2s ease',
            }}
          />

          {/* Лист снизу */}
          <div style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            background: '#fff',
            borderRadius: '20px 20px 0 0',
            padding: '18px 18px calc(18px + env(safe-area-inset-bottom))',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
            transform: shown ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 0.25s ease',
            willChange: 'transform',
            maxWidth: 520, margin: '0 auto',
          }}>
            {/* «Ручка» */}
            <div style={{
              width: 36, height: 4, borderRadius: 2, background: '#e5e7eb',
              margin: '0 auto 14px',
            }} />

            <div style={{
              fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4,
            }}>
              Обратная связь
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
              Нашли проблему или есть идея?
            </div>

            {/* Сегмент-контрол типа */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {TYPES.map((t) => {
                const active = t.key === type;
                return (
                  <button
                    key={t.key}
                    onClick={() => setType(t.key)}
                    disabled={sending}
                    style={{
                      flex: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '9px 6px',
                      borderRadius: 10,
                      border: active ? '1.5px solid #6366f1' : '1.5px solid #e5e7eb',
                      background: active ? '#eef2ff' : '#fff',
                      color: active ? '#4338ca' : '#374151',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                  >
                    <span style={{ fontSize: 15 }}>{t.emoji}</span>{t.label}
                  </button>
                );
              })}
            </div>

            {/* Текст */}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
              placeholder={PLACEHOLDERS[type]}
              rows={4}
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '11px 12px',
                border: '1.5px solid #e5e7eb', borderRadius: 12,
                fontSize: 14, color: '#111827', resize: 'vertical',
                outline: 'none', fontFamily: 'inherit',
                background: '#f9fafb',
              }}
            />
            <div style={{
              textAlign: 'right', fontSize: 11, color: '#9ca3af', marginTop: 4,
            }}>
              {message.length}/{MAX_LEN}
            </div>

            {/* Кнопки */}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button
                onClick={close}
                disabled={sending}
                style={{
                  flex: 1, padding: '13px', borderRadius: 12,
                  border: '1.5px solid #d1d5db', background: '#fff',
                  color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleSubmit}
                disabled={!message.trim() || sending}
                style={{
                  flex: 2, padding: '13px', borderRadius: 12, border: 'none',
                  background: message.trim() && !sending
                    ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : '#e5e7eb',
                  color: message.trim() && !sending ? '#fff' : '#9ca3af',
                  fontSize: 14, fontWeight: 700,
                  cursor: message.trim() && !sending ? 'pointer' : 'not-allowed',
                }}
              >
                {sending ? 'Отправка…' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
